import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Box, Typography } from '@mui/material'
import { formatInTimeZone } from 'date-fns-tz'
import { parseIsoDateTimeAsBerlin } from '../../utils/functions/timeUtils'
import { useTranslation } from 'react-i18next'

const BERLIN_TIME_ZONE = 'Europe/Berlin'

export const MonthlyClosing = () => {
    const { t, i18n } = useTranslation()
    const operationalPeriods = useSelector((state: any) => state.operationalPeriods?.value || [])

    const isDe = i18n.language?.startsWith('de')
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

    const closingPeriod = useMemo(() => operationalPeriods.find((p: any) => p.Type === 0), [operationalPeriods])
    const fromText = closingPeriod?.From
    const toText = closingPeriod?.To
    const effectiveText = closingPeriod?.Effective

    const effective = useMemo(() => parseIsoDateTimeAsBerlin(effectiveText), [effectiveText])
    const [nowMs, setNowMs] = useState(() => Date.now())

    useEffect(() => {
        if (!effective) return
        const delayMs = effective.getTime() - Date.now()
        if (delayMs <= 0) return
        const timeoutId = window.setTimeout(() => setNowMs(Date.now()), delayMs + 50)
        return () => window.clearTimeout(timeoutId)
    }, [effective?.getTime()])

    const periodFormatter = useMemo(() => {
        return new Intl.DateTimeFormat(isDe ? 'de-DE' : 'en-US', {
            timeZone: BERLIN_TIME_ZONE,
            day: '2-digit',
            month: '2-digit',
        })
    }, [isDe])

    const formatPeriodDayMonth = (date: Date) => {
        const formatted = periodFormatter.format(date)
        return isDe && !formatted.endsWith('.') ? `${formatted}.` : formatted
    }

    const periodStr = useMemo(() => {
        if (!fromText || !toText) return null
        const from = parseIsoDateTimeAsBerlin(fromText)
        const to = parseIsoDateTimeAsBerlin(toText)
        if (!from || !to) return null
        return `${formatPeriodDayMonth(from)} - ${formatPeriodDayMonth(to)}`
    }, [fromText, toText, periodFormatter, isDe])

    const effectiveTextLine = useMemo(() => {
        if (!effective) return ''
        const datePattern = isDe ? 'dd.MM.yyyy' : 'MM/dd/yyyy'
        const datePart = formatInTimeZone(effective, userTimeZone, datePattern)
        const timePart = formatInTimeZone(effective, userTimeZone, 'HH:mm zzz')
        return `${datePart} ${t('monthlyclosing.at')} ${timePart}`
    }, [effective, isDe, userTimeZone, t])

    const shouldRender = Boolean(fromText && toText) && (!effective || nowMs < effective.getTime())
    if (!shouldRender) return <></>

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', backgroundColor: 'background.paper', m: 4, marginTop: 4 }}>
            <Box sx={{ textAlign: 'center', width: 350, p: 2}}>
                <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 700 }}>
                    {t('monthlyclosing.alertTitle')}
                </Typography>
                <Typography sx={{ mt: 1, fontSize: '14px' }}>{t('monthlyclosing.alertMessage', { period: periodStr })}</Typography>
                <Typography sx={{ fontSize: '14px' }}>{t('monthlyclosing.alertMessage2')}</Typography>
                <Typography variant="body2" sx={{ marginTop: 1, fontSize: '14px', fontWeight: 'bold' }}>
                    {effectiveTextLine}
                </Typography>
                <Typography variant="body2" sx={{ marginTop: 1, fontSize: '14px' }}>
                    {t('monthlyclosing.reminder')}
                </Typography>
            </Box>
        </Box>
    )
}
